/**
 * The BIMGO-credit wallet — what the Billing & Plan tab reads and writes.
 * Ported from `bimgo-ai-web`'s `tokensApi`, shaped like its sibling
 * `lib/usage.ts` (#64).
 *
 * Seven endpoints on **api-v2**, all org-scoped and all behind the `{ data }`
 * envelope. Reads are gated by `org:read`, writes by `org:manage_limits`:
 *
 *   - `GET  /organizations/{id}/tokens`              → balance + expiry rollup
 *   - `GET  /organizations/{id}/tokens/purchases`    → the additions ledger
 *   - `GET  /organizations/{id}/limits`              → the org's cap
 *   - `PUT  /organizations/{id}/limits`              → set/clear it
 *   - `GET  /organizations/{id}/members/limits`      → every member's cap
 *   - `PUT  /organizations/{id}/members/{uid}/limit` → set/clear one
 *   - `PUT  /organizations/{id}/members/limits`      → set every one at once
 *
 * api-v2 also serves `tokens/consumption` and `users/{uid}/tokens/consumption`
 * — per-member wallet spend, and one member's per-request rows. `bimgo-ai-web`
 * still exports clients for both and no longer renders either; the desktop does
 * not want that table at all (owner, 2026-09-03), so it does not carry a client
 * for an endpoint nothing here calls.
 *
 * Everything is in BIMGO credits; there is no money concept on the client.
 *
 * Three shapes to respect rather than tidy:
 *
 *   - **`null` means "no cap / inherit"; `0` is a real cap meaning "may spend
 *     nothing".** They must stay distinguishable end to end — the same trap
 *     `usage.ts` records for quotas, and the reason nothing here writes
 *     `value || null` (0 is falsy and would silently become "no cap").
 *   - **api-v2 serialises Postgres `numeric` as a string.** A 7,500,000 cap
 *     arrives as `"7500000.0000"`, and `String.prototype.toLocaleString` hands
 *     such a value straight back — which is how raw decimals reach the screen
 *     (`bimgo-ai-web` #177). v2 coerces at each render site and had to be fixed
 *     once per site; this module coerces **at the boundary** instead, so the
 *     types above are true and a component cannot forget. `toNumber` keeps 0 and
 *     only maps the genuinely absent to null.
 *   - **`email` on a member row is nullable**, exactly as in `usage.ts`: the
 *     backend may fail to read `auth.users` depending on deployment grants. The
 *     row still carries a real cap and real spend, so it degrades to the user id
 *     rather than being dropped.
 *
 * A **403 keeps its status** rather than being folded into "unauthenticated":
 * on these calls it means "not an org admin", not "signed out", and signing a
 * working session out over it would be a real bug.
 */
import { OrganizationsError, readDetail } from "./organizations";
import { bearer } from "./accessToken";
import type { BillingMode } from "./usage";

// -- wire shapes -------------------------------------------------------------

/**
 * Declared in `lib/usage.ts` and re-exported here: the usage endpoints carry
 * `billing_mode` too, and one org cannot be `per_seat` on one screen and
 * `org_shared` on another.
 */
export type { BillingMode };

export interface TokenOverview {
  wallet_balance: number;
  has_active_subscription: boolean;
  /** Earliest upcoming lot expiry (ISO), or null when nothing is due. */
  next_expiry_at: string | null;
  /** Credits expiring within the server's "soon" window. */
  expiring_soon_credits: number;
  /** Credits left on already-expired lots — not part of the balance. */
  expired_total: number;
}

export interface TokenPurchaseItem {
  occurred_at: string;
  /** `grant` | `topup` | `adjustment` | `refund` — the server's own word. */
  type: string;
  bimgo_credit: number;
  balance_after: number | null;
  reference: string | null;
  /** The credit lot's expiry (ISO), null when it does not expire. */
  expires_at: string | null;
}

export interface OrganizationLimits {
  billing_mode: BillingMode;
  /** Cap on org-token spend for the whole org this cycle; null = unlimited. */
  monthly_credit_limit: number | null;
  /** True when the cap was computed (seats × plan cap) rather than admin-set. */
  monthly_credit_limit_is_default: boolean;
  /** Org-token credits already spent this cycle, against that cap. */
  org_credits_spent: number;
  /** ISO, UTC — the window the figures above are measured over. */
  cycle_start: string | null;
  cycle_end: string | null;
}

/**
 * Where a member's cap came from: the admin's own value (`member`); the
 * per-seat default of 0, i.e. no pay-as-you-go budget (`default`); or no
 * per-member ceiling at all (`unlimited`), which is the org_shared default.
 */
export type LimitSource = "member" | "default" | "unlimited";

export interface MemberLimit {
  user_id: string;
  email: string | null;
  /** Resolved cap on WALLET (PAYG) cycle spend; null = unlimited. */
  effective_limit: number | null;
  limit_source: LimitSource;
  /** The member's own stored cap, if set — this is what the dialog edits. */
  member_limit: number | null;
  /** Wallet draws only — a seat member inside their prepaid allowance reads 0. */
  spent: number;
  /** Null when `effective_limit` is null: nothing to count down from. */
  remaining: number | null;
  exhausted: boolean;
}

/**
 * The member rows plus the org's plan mode, which decides what is spent before
 * the cap applies. api-v2 carries it on the same response, so the members card
 * needs no second request for the org limits.
 */
export interface MemberLimitsResult {
  billing_mode: BillingMode;
  members: MemberLimit[];
}

// -- numerics ----------------------------------------------------------------

/**
 * Coerce an api-v2 numeric to a JS number.
 *
 * Returns null for anything that is not a finite number — null, undefined and
 * `""` included. **0 is preserved**: it is a real cap, not an absent one.
 */
export function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** The same, for a field the server always sends: an absent one reads as 0. */
function count(value: unknown): number {
  return toNumber(value) ?? 0;
}

/**
 * Grouped, whole-number display for credit counts: `"7500000.0000"` →
 * `"7,500,000"`. These are counted units, never fractional, so a decimal tail
 * from the wire is rounded away rather than shown. Null renders as an em dash.
 */
export function formatCredits(value: unknown, locale?: string): string {
  const n = toNumber(value);
  if (n === null) return "—";
  return Math.round(n).toLocaleString(locale);
}

/**
 * A limit input's text → the value to send. `""` maps to null (clear/inherit);
 * a non-negative number maps to itself, **0 included**. Commas are tolerated
 * because a pasted figure usually carries them.
 */
export function parseLimitInput(raw: string): { ok: boolean; value: number | null } {
  const trimmed = raw.trim();
  if (trimmed === "") return { ok: true, value: null };
  const n = Number(trimmed.replace(/,/g, ""));
  if (!Number.isFinite(n) || n < 0) return { ok: false, value: null };
  return { ok: true, value: n };
}

/** The inverse: null → `""` so an empty box round-trips as "inherit"; 0 stays `"0"`. */
export function limitToInput(value: number | null | undefined): string {
  const n = toNumber(value);
  return n === null ? "" : String(Math.round(n));
}

/**
 * How far through the subscription window we are, and how long is left.
 *
 * Pure so the one thing this card exists to say — *your organization lapses on
 * this date* — can be asserted without a clock or a browser. `percent` is
 * clamped to 0–100 and `daysLeft` never goes negative: a lapsed subscription
 * reads "0 days left", not a negative countdown.
 */
export function subscriptionProgress(
  startsAt: string,
  endsAt: string,
  nowMs: number = Date.now(),
): { percent: number; daysLeft: number } {
  const start = new Date(startsAt).getTime();
  const end = new Date(endsAt).getTime();
  // An unparseable date is not a reason to render NaN across the card.
  if (!Number.isFinite(start) || !Number.isFinite(end)) return { percent: 0, daysLeft: 0 };
  const total = Math.max(1, end - start);
  return {
    percent: Math.min(100, Math.max(0, Math.round(((nowMs - start) / total) * 100))),
    daysLeft: Math.max(0, Math.ceil((end - nowMs) / 86_400_000)),
  };
}

// -- transport ---------------------------------------------------------------

export interface TokensDeps {
  apiBaseUrl: string;
  fetchImpl?: typeof fetch;
}

function base(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/+$/, "");
}

function org(apiBaseUrl: string, orgId: string): string {
  return `${base(apiBaseUrl)}/organizations/${encodeURIComponent(orgId)}`;
}

/** The whole envelope — `getMemberLimits` needs a field beside `data`. */
async function request(
  method: "GET" | "PUT",
  url: string,
  token: string | null,
  body: unknown,
  fetchImpl: typeof fetch,
): Promise<Record<string, unknown>> {
  if (!token) throw new OrganizationsError({ kind: "unauthenticated" }, "not signed in");

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method,
      headers: {
        Authorization: bearer(token),
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (error) {
    throw new OrganizationsError({ kind: "network", detail: String(error) }, String(error));
  }
  if (response.status === 401) {
    throw new OrganizationsError({ kind: "unauthenticated" }, "session rejected by the API");
  }

  const parsed: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = readDetail(parsed);
    // 403 keeps its status — see the header.
    throw new OrganizationsError(
      { kind: "http", status: response.status, detail },
      detail ?? `HTTP ${response.status}`,
    );
  }
  return (parsed ?? {}) as Record<string, unknown>;
}

async function data(
  method: "GET" | "PUT",
  url: string,
  token: string | null,
  body: unknown,
  fetchImpl: typeof fetch,
): Promise<unknown> {
  return (await request(method, url, token, body, fetchImpl))["data"];
}

// -- normalisers -------------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};
}

function text(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function overview(value: unknown): TokenOverview {
  const d = asRecord(value);
  return {
    wallet_balance: count(d["wallet_balance"]),
    has_active_subscription: d["has_active_subscription"] === true,
    next_expiry_at: text(d["next_expiry_at"]),
    expiring_soon_credits: count(d["expiring_soon_credits"]),
    expired_total: count(d["expired_total"]),
  };
}

function purchase(value: unknown): TokenPurchaseItem {
  const d = asRecord(value);
  return {
    occurred_at: text(d["occurred_at"]) ?? "",
    type: text(d["type"]) ?? "",
    bimgo_credit: count(d["bimgo_credit"]),
    balance_after: toNumber(d["balance_after"]),
    reference: text(d["reference"]),
    expires_at: text(d["expires_at"]),
  };
}

function billingMode(value: unknown): BillingMode {
  // Anything unrecognised reads as per_seat, the mode whose figures the plan
  // card is safe to show: org_shared hides fields, and hiding them on a guess
  // would blank a card that has real numbers in it.
  return value === "org_shared" ? "org_shared" : "per_seat";
}

function limits(value: unknown): OrganizationLimits {
  const d = asRecord(value);
  return {
    billing_mode: billingMode(d["billing_mode"]),
    monthly_credit_limit: toNumber(d["monthly_credit_limit"]),
    monthly_credit_limit_is_default: d["monthly_credit_limit_is_default"] !== false,
    org_credits_spent: count(d["org_credits_spent"]),
    cycle_start: text(d["cycle_start"]),
    cycle_end: text(d["cycle_end"]),
  };
}

function memberLimit(value: unknown): MemberLimit {
  const d = asRecord(value);
  const source = d["limit_source"];
  return {
    user_id: text(d["user_id"]) ?? "",
    email: text(d["email"]),
    effective_limit: toNumber(d["effective_limit"]),
    limit_source:
      source === "member" || source === "default" || source === "unlimited" ? source : "default",
    member_limit: toNumber(d["member_limit"]),
    spent: count(d["spent"]),
    remaining: toNumber(d["remaining"]),
    exhausted: d["exhausted"] === true,
  };
}

function list<T>(value: unknown, map: (item: unknown) => T): T[] {
  return Array.isArray(value) ? value.map(map) : [];
}

// -- reads -------------------------------------------------------------------

/** Current wallet balance, the active-subscription flag and the expiry rollup. */
export async function getOrgTokenOverview(
  token: string | null,
  orgId: string,
  deps: TokensDeps,
): Promise<TokenOverview> {
  return overview(
    await data("GET", `${org(deps.apiBaseUrl, orgId)}/tokens`, token, undefined, deps.fetchImpl ?? fetch),
  );
}

/** Additions ledger — grants / top-ups / adjustments / refunds, newest first. */
export async function getOrgTokenPurchases(
  token: string | null,
  orgId: string,
  page: { limit?: number; offset?: number } = {},
  deps: TokensDeps,
): Promise<TokenPurchaseItem[]> {
  const query = `limit=${page.limit ?? 100}&offset=${page.offset ?? 0}`;
  return list(
    await data(
      "GET",
      `${org(deps.apiBaseUrl, orgId)}/tokens/purchases?${query}`,
      token,
      undefined,
      deps.fetchImpl ?? fetch,
    ),
    purchase,
  );
}

/** The org's monthly cap and where it stands in the current cycle. */
export async function getOrganizationLimits(
  token: string | null,
  orgId: string,
  deps: TokensDeps,
): Promise<OrganizationLimits> {
  return limits(
    await data("GET", `${org(deps.apiBaseUrl, orgId)}/limits`, token, undefined, deps.fetchImpl ?? fetch),
  );
}

/** Every member's effective cap, its source, and their wallet spend this cycle. */
export async function getMemberLimits(
  token: string | null,
  orgId: string,
  deps: TokensDeps,
): Promise<MemberLimitsResult> {
  const body = await request(
    "GET",
    `${org(deps.apiBaseUrl, orgId)}/members/limits`,
    token,
    undefined,
    deps.fetchImpl ?? fetch,
  );
  return {
    billing_mode: billingMode(body["billing_mode"]),
    members: list(body["data"], memberLimit),
  };
}

// -- writes ------------------------------------------------------------------

/**
 * Set or clear the org's monthly cap. **null restores the computed default**
 * (seats × plan allowance) — it is not "unlimited", and 0 is not null.
 */
export async function updateOrganizationLimits(
  token: string | null,
  orgId: string,
  monthlyCreditLimit: number | null,
  deps: TokensDeps,
): Promise<OrganizationLimits> {
  return limits(
    await data(
      "PUT",
      `${org(deps.apiBaseUrl, orgId)}/limits`,
      token,
      { monthly_credit_limit: monthlyCreditLimit },
      deps.fetchImpl ?? fetch,
    ),
  );
}

/** Set or clear one member's cap. null falls them back to the plan cap. */
export async function updateMemberLimit(
  token: string | null,
  orgId: string,
  userId: string,
  monthlyCreditLimit: number | null,
  deps: TokensDeps,
): Promise<MemberLimit> {
  return memberLimit(
    await data(
      "PUT",
      `${org(deps.apiBaseUrl, orgId)}/members/${encodeURIComponent(userId)}/limit`,
      token,
      { monthly_credit_limit: monthlyCreditLimit },
      deps.fetchImpl ?? fetch,
    ),
  );
}

/**
 * Set every member's cap at once — the "apply to the whole organization"
 * action. api-v2 has no org-level default column; an org-wide cap is a batch
 * write across members, so a member's cap always has exactly one origin.
 * null clears everyone back to the plan cap.
 */
export async function updateAllMemberLimits(
  token: string | null,
  orgId: string,
  monthlyCreditLimit: number | null,
  deps: TokensDeps,
): Promise<MemberLimit[]> {
  return list(
    await data(
      "PUT",
      `${org(deps.apiBaseUrl, orgId)}/members/limits`,
      token,
      { monthly_credit_limit: monthlyCreditLimit },
      deps.fetchImpl ?? fetch,
    ),
    memberLimit,
  );
}
